import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSearchParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getRequestWithToken } from "../../utils/ApiRequests";
import { axiosInstance } from "../../axiosconfing";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";

const getDefaultRegistrationFrom = (date = new Date()) => `${date.getFullYear()}-06-01`;
const DEFAULT_REGISTRATION_FROM = getDefaultRegistrationFrom();
const pageSizeOptions = [10, 25, 50, 100];

const onboardingOptions = [
  { value: "", label: "All" },
  { value: "complete", label: "Complete" },
  { value: "incomplete", label: "Incomplete" },
];

const regFormStatusOptions = [
  { value: "", label: "All Steps" },
  { value: "0", label: "0 - Not Started" },
  // { value: "1", label: "1 - Signup Started" },
  { value: "2", label: "2 - Basic Details Completed" },
  { value: "3", label: "3 - Role And Languages Completed" },
  { value: "4", label: "4 - Job Preferences Completed" },
  { value: "5", label: "5 - Completed" },
];

const getRegFormStatusLabel = (value) => regFormStatusOptions.find((item) => item.value === String(value))?.label || value || "0 - Not Started";

const buildQuery = (filters, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  return params.toString();
};

const formatDate = (value) => value ? new Date(value).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-";
const todayDateInput = () => new Date().toISOString().slice(0, 10);

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const StatCard = ({ label, value }) => (
  <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>{value ?? 0}</Typography>
  </Paper>
);

const CandidateAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    registrationFrom: searchParams.get("registrationFrom") || DEFAULT_REGISTRATION_FROM,
    registrationTo: searchParams.get("registrationTo") || "",
    onboardingStatus: searchParams.get("onboardingStatus") || "",
    regFormStatus: searchParams.get("regFormStatus") || "",
    state: searchParams.get("state") || "",
    district: searchParams.get("district") || "",
    city: searchParams.get("city") || "",
    hasApplied: searchParams.get("hasApplied") || "",
  });
  const [summaryData, setSummaryData] = useState({ summary: {}, trend: [], funnel: [], roles: [] });
  const [candidateTables, setCandidateTables] = useState({ incomplete: { data: [], pagination: {} } });
  const [pagination, setPagination] = useState({
    incomplete: { page: 1, limit: 25 },
  });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState({ incomplete: false });
  const [exporting, setExporting] = useState("");
  const [error, setError] = useState("");

  const query = useMemo(() => buildQuery(filters), [filters]);

  const loadSummary = async () => {
    setLoading(true);
    setError("");
    try {
      const [summary, trend, funnel, roles] = await Promise.all([
        getRequestWithToken(`admin/candidate-analytics/summary?${query}`),
        getRequestWithToken(`admin/candidate-analytics/registration-trend?${query}`),
        getRequestWithToken(`admin/candidate-analytics/onboarding-funnel?${query}`),
        getRequestWithToken(`admin/candidate-analytics/roles?${query}`),
      ]);
      setSummaryData({
        summary: summary.data?.data || {},
        trend: trend.data?.data || [],
        funnel: (funnel.data?.data || []).filter((item) => String(item.regFormStatus) !== "complete"),
        roles: roles.data?.data || [],
      });
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to load candidate analytics.");
    } finally {
      setLoading(false);
    }
  };

  const loadCandidateTable = async (tableKey) => {
    setTableLoading((prev) => ({ ...prev, [tableKey]: true }));
    try {
      const extra = { onboardingStatus: "incomplete", page: pagination.incomplete.page, limit: pagination.incomplete.limit };
      const res = await getRequestWithToken(`admin/candidate-analytics/candidates?${buildQuery(filters, extra)}`);
      setCandidateTables((prev) => ({
        ...prev,
        [tableKey]: {
          data: res.data?.data || [],
          pagination: res.data?.pagination || { page: extra.page, limit: extra.limit, total: res.data?.totalcount || 0, totalPages: 1 },
        },
      }));
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to load candidate table.");
    } finally {
      setTableLoading((prev) => ({ ...prev, [tableKey]: false }));
    }
  };

  useEffect(() => { loadSummary(); }, [query]);
  useEffect(() => { loadCandidateTable("incomplete"); }, [query, pagination.incomplete.page, pagination.incomplete.limit]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
    setSearchParams(params);
    setPagination({ incomplete: { page: 1, limit: pagination.incomplete.limit } });
  };

  const clearFilters = () => {
    const next = { registrationFrom: DEFAULT_REGISTRATION_FROM, registrationTo: "", onboardingStatus: "", regFormStatus: "", state: "", district: "", city: "", hasApplied: "" };
    setFilters(next);
    setSearchParams({ registrationFrom: DEFAULT_REGISTRATION_FROM });
    setPagination({ incomplete: { page: 1, limit: 25 } });
  };

  const exportTable = async (type, label) => {
    setExporting(type);
    setError("");
    try {
      const response = await axiosInstance.get(`admin/candidate-analytics/export/${type}?${query}`, { responseType: "blob" });
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(new Blob([response.data], { type: response.headers["content-type"] }), `${label}-${today}.xlsx`);
    } catch (err) {
      setError(err?.response?.data?.msg || `Unable to export ${label}.`);
    } finally {
      setExporting("");
    }
  };

  const changeTablePage = (tableKey, page) => {
    setPagination((prev) => ({ ...prev, [tableKey]: { ...prev[tableKey], page: page + 1 } }));
  };

  const changeTableLimit = (tableKey, limit) => {
    setPagination((prev) => ({ ...prev, [tableKey]: { page: 1, limit } }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Candidate Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Operational candidate counts from Mongo records.</Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" type="date" label="Registered From" InputLabelProps={{ shrink: true }} inputProps={{ max: todayDateInput() }} value={filters.registrationFrom} onChange={(e) => setFilters((p) => ({ ...p, registrationFrom: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" type="date" label="Registered To" InputLabelProps={{ shrink: true }} inputProps={{ max: todayDateInput() }} value={filters.registrationTo} onChange={(e) => setFilters((p) => ({ ...p, registrationTo: e.target.value }))} /></Grid>
            {/* <Grid item xs={12} md={2}>
                <TextField select fullWidth size="small" label="Onboarding" value={filters.onboardingStatus} onChange={(e) => setFilters((p) => ({ ...p, onboardingStatus: e.target.value }))}>{onboardingOptions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </TextField>
              </Grid> */}
            <Grid item xs={12} md={2}><TextField select fullWidth size="small" label="Onboarding Status" value={filters.regFormStatus} onChange={(e) => setFilters((p) => ({ ...p, regFormStatus: e.target.value }))}>{regFormStatusOptions.map((item) => <MenuItem key={item.value || 'all'} value={item.value}>{item.label}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" label="State" value={filters.state} onChange={(e) => setFilters((p) => ({ ...p, state: e.target.value }))} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth size="small" label="District" value={filters.district} onChange={(e) => setFilters((p) => ({ ...p, district: e.target.value }))} /></Grid>
            {/* <Grid item xs={12} md={2}><TextField fullWidth size="small" label="City" value={filters.city} onChange={(e) => setFilters((p) => ({ ...p, city: e.target.value }))} /></Grid> */}
            <Grid item xs={12} md={2}><TextField select fullWidth size="small" label="Has Applied" value={filters.hasApplied} onChange={(e) => setFilters((p) => ({ ...p, hasApplied: e.target.value }))}><MenuItem value="">All</MenuItem><MenuItem value="true">Yes</MenuItem><MenuItem value="false">No</MenuItem></TextField></Grid>
            <Grid item xs={12} md={8}><Stack direction="row" spacing={1}><Button variant="contained" onClick={applyFilters}>Apply</Button><Button variant="outlined" onClick={clearFilters}>Reset</Button></Stack></Grid>
          </Grid>
        </Paper>

        {error && <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "error.light", color: "error.main" }}>{error}</Paper>}

        {loading ? <ReusableLoader /> : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2.4}><StatCard label="Registered" value={summaryData.summary.totalCandidates} /></Grid>
              <Grid item xs={12} md={2.4}><StatCard label="Onboarding Complete" value={summaryData.summary.onboardingCompleted} /></Grid>
              <Grid item xs={12} md={2.4}><StatCard label="Role Selected" value={summaryData.summary.withRoleSelection} /></Grid>
              <Grid item xs={12} md={2.4}><StatCard label="Applied Candidates" value={summaryData.summary.appliedCandidates} /></Grid>
              <Grid item xs={12} md={2.4}><StatCard label="Applications" value={summaryData.summary.totalApplications} /></Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, height: 320 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Registration Trend</Typography>
                  <ResponsiveContainer width="100%" height="85%"><LineChart data={summaryData.trend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" /><YAxis allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="count" stroke="#f26d3d" strokeWidth={2} /></LineChart></ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, height: 380 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Onboarding Funnel</Typography>
                  <ResponsiveContainer width="100%" height="85%"><BarChart data={summaryData.funnel} margin={{ top: 8, right: 16, left: 8, bottom: 80 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" interval={0} height={90} angle={-35} textAnchor="end" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill="#1976d2" /></BarChart></ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            <AnalyticsTable title="Candidates by Role" rows={summaryData.roles} columns={[{ key: "family", label: "Family" }, { key: "group", label: "Group" }, { key: "role", label: "Role" }, { key: "candidateCount", label: "Candidates" }, { key: "appliedCandidates", label: "Applied" }, { key: "conversionRate", label: "Conversion %" }]} onExport={() => exportTable("roles", "candidates-by-role")} exporting={exporting === "roles"} />
            <AnalyticsTable title="Incomplete Candidates" rows={candidateTables.incomplete.data} columns={[{ key: "name", label: "Name" }, { key: "mobile", label: "Mobile" }, { id: "onboarding", label: "Onboarding", accessor: (row) => getRegFormStatusLabel(row.reg_form_status) }, { key: "createdAt", label: "Registered", format: formatDate }]} loading={tableLoading.incomplete} pagination={candidateTables.incomplete.pagination} onPageChange={(page) => changeTablePage("incomplete", page)} onRowsPerPageChange={(limit) => changeTableLimit("incomplete", limit)} onExport={() => exportTable("incomplete-candidates", "incomplete-candidates")} exporting={exporting === "incomplete-candidates"} />
          </>
        )}
      </Stack>
    </Box>
  );
};

const AnalyticsTable = ({ title, rows, columns, loading, pagination, onPageChange, onRowsPerPageChange, onExport, exporting }) => {
  const serialOffset = pagination ? ((pagination.page || 1) - 1) * (pagination.limit || 25) : 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Chip size="small" label={pagination?.total !== undefined ? `${pagination.total} records` : `${rows.length} rows`} />
        </Stack>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={exporting} onClick={onExport}>{exporting ? "Exporting..." : `Export ${title}`}</Button>
      </Stack>
      <TableContainer sx={{ maxHeight: 420, overflowX: "auto", overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow><TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>S.No.</TableCell>{columns.map((column) => <TableCell key={column.id || column.key} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{column.label}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={columns.length + 1}>Loading...</TableCell></TableRow> : rows.length === 0 ? <TableRow><TableCell colSpan={columns.length + 1}>No records found</TableCell></TableRow> : rows.map((row, index) => (
              <TableRow key={row._id || row.jobId || index}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{serialOffset + index + 1}</TableCell>
                {columns.map((column) => {
                  const value = column.accessor ? column.accessor(row) : row[column.key];
                  return <TableCell key={column.id || column.key} sx={{ whiteSpace: "nowrap" }}>{column.format ? column.format(value, row) : String(value ?? "-")}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={Math.max((pagination.page || 1) - 1, 0)}
          rowsPerPage={pagination.limit || 25}
          rowsPerPageOptions={pageSizeOptions}
          onPageChange={(event, page) => onPageChange(page)}
          onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        />
      )}
    </Paper>
  );
};

export default CandidateAnalytics;
